import { Button, IconButton, Typography } from "@suraasa/placebo-ui"
import { useMediaQuery } from "@uidotdev/usehooks"
import clsx from "clsx"
import AsyncBuilder from "components/AsyncBuilder"
import SuraasaPlusLogo, {
  LogoVariant,
} from "features/SuraasaPlus/components/SuraasaPlusLogo"
import { useSuraasaPlusContext } from "features/SuraasaPlus/context"
import { getSuraasaPlusLandingPageUrl } from "features/SuraasaPlus/helper"
import { Xmark } from "iconoir-react"

import lightOverlay from "../assets/light_layer.webp"
import darkOverlay from "../assets/top_dark_layer.webp"
import { Categories, DialogThemeMode, getSuraasaPlusPlans } from "../utils"
import AdaptiveDialogAndSheet from "./AdaptiveDialogAndSheets"

type Props = {
  open: boolean
  handleClose: () => void
  mode?: DialogThemeMode
  onClick: () => Promise<void>
}

const modeStyles = {
  [DialogThemeMode.dark]: {
    body: "bg-[#0E1525]",
    freeBg: "bg-[#1A2130]",
    plusBg: "bg-[#312F28]",
    header:
      "bg-[linear-gradient(180deg,#4C1278_0%,#082743_56.82%,#0E1525_100%)]",
    footer: "bg-gradient-to-b from-[#0E1525]/0 via-[#0E1525] to-[#0E1525]",
    border: "border-[#CBD5E117]",
    text: {
      primary1: "text-surface",
      primary2: "text-highlight-100",
      secondary: "text-onSurface-400",
      section: "text-onSurface-100 sm:text-onSurface-400",
      freeHeading: "text-onSurface-300",
      plusHeading: "#FFDF77",
      free: "text-onSurface-400",
      plus: "text-[#FFF3CC]",
      label: "text-onSurface-200",
    },
    buttons: {
      x: "text-surface hover:bg-onSurface-200/20  active:bg-onSurface-300/40 active:text-onSurface-900",
      text: "text-onSurface-200 hover:bg-white/20 active:bg-white/30",
      primary: "bg-surface text-onSurface-900",
    },
  },
  [DialogThemeMode.light]: {
    body: "bg-white",
    freeBg: "bg-surface-100",
    plusBg: "bg-[#F9F3E1]",
    header: "bg-white",
    footer: "bg-gradient-to-b from-white/0 via-white to-white",
    border: "border-[#485D7717]",
    text: {
      primary1: "text-onSurface-900",
      primary2: "text-onSurface-600",
      secondary: "text-onSurface-500",
      section: "text-onSurface-900 sm:text-onSurface-600",
      freeHeading: "text-onSurface-700",
      plusHeading: "#9E801E",
      free: "text-onSurface-600",
      plus: "text-[#61511C]",
      label: "text-onSurface-800",
    },
    buttons: {
      x: "text-onSurface-700 hover:bg-gray-100 active:bg-gray-200",
      text: "text-onSurface-700 hover:bg-gray-100 active:bg-gray-200",
      primary: "bg-onSurface-900 text-white",
    },
  },
}

const FeatureOverviewDialog = ({
  open,
  handleClose,
  mode = DialogThemeMode.dark,
  onClick,
}: Props) => {
  const styles = modeStyles[mode]
  // TODO: isNewUser is temporarily set to true because its state is shared between both
  // "try" and "get" flows. The false state is related to pgctl flow thus will be used later.
  const isNewUser = true
  const masterClassDialog = false

  const isSmDown = useMediaQuery("only screen and (max-width : 640px)")

  const { aiToolGenerationsAllowed, jobApplicationsAllowed } =
    useSuraasaPlusContext()

  const plans = getSuraasaPlusPlans({
    isMobile: isSmDown,
    modalVariant: true,
    aiToolsFreeLimit:
      aiToolGenerationsAllowed === -1 ? 0 : aiToolGenerationsAllowed,
    jobsFreeLimit: jobApplicationsAllowed,
  })

  // const orderedPlans = masterClassDialog
  //   ? [
  //       ...plans.filter(p => p.category === Categories.masterclasses),
  //       ...plans.filter(p => p.category === Categories.aiTools),
  //       ...plans.filter(
  //         p =>
  //           p.category !== Categories.masterclasses &&
  //           p.category !== Categories.aiTools
  //       ),
  //     ]
  //   : plans
  const orderedPlans = plans

  const overlayImg = mode === DialogThemeMode.dark ? darkOverlay : lightOverlay

  return (
    <AdaptiveDialogAndSheet
      sheetHeight={99}
      body={
        <>
          <div
            className={clsx(
              "relative px-3 pb-0 pt-3.5 sm:px-4 sm:pb-1",
              styles.header
            )}
          >
            <img
              src={overlayImg}
              alt=""
              className="pointer-events-none absolute -top-px left-0 z-0"
            />
            <div className="flex justify-end">
              <IconButton
                className={clsx("rounded-lg", styles.buttons.x)}
                onClick={handleClose}
              >
                <Xmark className="!size-2.5" strokeWidth={2} />
              </IconButton>
            </div>
            <div className="relative z-2">
              {isNewUser ? (
                <>
                  {masterClassDialog ? (
                    <>
                      <Typography
                        className={clsx(
                          "w-fit max-w-[265px] text-largeTitleMobile",
                          mode === DialogThemeMode.light
                            ? styles.text.primary2
                            : styles.text.primary1
                        )}
                      >
                        Unlock Live
                      </Typography>
                      <Typography
                        className={clsx(
                          "w-fit max-w-[265px] text-largeTitleMobile",
                          styles.text.primary2
                        )}
                      >
                        Masterclasses & more with
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography
                        className={clsx(
                          "w-fit max-w-[200px] text-largeTitleMobile",
                          mode === DialogThemeMode.light
                            ? styles.text.primary2
                            : styles.text.primary1
                        )}
                      >
                        Unlock
                      </Typography>
                      <Typography
                        className={clsx(
                          "w-fit max-w-[200px] text-largeTitleMobile",
                          styles.text.primary2
                        )}
                      >
                        more with
                      </Typography>
                    </>
                  )}
                  <SuraasaPlusLogo
                    height={36}
                    width={204}
                    color={mode === DialogThemeMode.dark ? "light" : "dark"}
                  />
                </>
              ) : (
                <>
                  <SuraasaPlusLogo
                    height={36}
                    width={204}
                    color={mode === DialogThemeMode.dark ? "light" : "dark"}
                  />
                  <Typography
                    className={clsx(
                      "mt-0.5 w-fit text-largeTitleMobile",
                      styles.text.primary2
                    )}
                  >
                    is now yours!
                  </Typography>
                </>
              )}
              {!isNewUser && (
                <Typography
                  className={clsx(
                    "mt-2 max-w-[312px] sm:max-w-[500px]",
                    styles.text.secondary
                  )}
                  variant="largeBody"
                >
                  Enjoy 1 year of Suraasa Plus completely free, included with
                  your PgCTL enrolment.
                </Typography>
              )}
            </div>
          </div>
          <div className={clsx(styles.body, "px-2.5 py-2 sm:px-4")}>
            <div className="relative mb-16 sm:mb-11">
              <div className="absolute inset-0 grid grid-cols-2 gap-0.5 sm:grid-cols-3">
                <div className="hidden sm:block" />
                <div className={clsx("rounded-xl", styles.freeBg)} />
                <div className={clsx("rounded-xl", styles.plusBg)} />
              </div>

              <div className="relative">
                <div
                  className={clsx(
                    "sticky top-0 z-10 grid grid-cols-2 gap-0.5 pt-2 transition-colors sm:grid-cols-3",
                    styles.body
                  )}
                >
                  <div className={clsx("hidden pb-1.5 sm:block", styles.body)}>
                    <Typography
                      variant="preTitle"
                      className={clsx("mt-2 max-w-15", styles.text.secondary)}
                    >
                      WHAT&apos;S INCLUDED
                    </Typography>
                  </div>
                  <div className={clsx("rounded-t-xl pb-1.5", styles.freeBg)}>
                    <Typography
                      variant="strongSmallBody"
                      className={clsx(
                        "ml-1.75 mt-2.5",
                        styles.text.freeHeading
                      )}
                    >
                      Free
                    </Typography>
                  </div>
                  <div className={clsx("rounded-t-xl pb-1.5", styles.plusBg)}>
                    <div className="ml-1.75 mt-2.5 w-fit">
                      <SuraasaPlusLogo
                        color={styles.text.plusHeading}
                        height={17}
                        width={36}
                        variant={LogoVariant.plus}
                      />
                    </div>
                  </div>
                </div>

                {orderedPlans.map((section, idx) => (
                  <div key={idx}>
                    <div
                      className={clsx("border-y p-1.5 sm:pl-0", styles.border)}
                    >
                      <Typography
                        variant={isSmDown ? "strongSmallBody" : "preTitle"}
                        className={clsx(styles.text.section)}
                      >
                        {section.category}
                      </Typography>
                    </div>

                    {section.items.map((item, i) => (
                      <div
                        key={i}
                        className={clsx(
                          "grid grid-cols-2 gap-0.5 py-1.75 sm:grid-cols-3",
                          {
                            "pb-[30px]": section.items.length === 1,
                          }
                        )}
                      >
                        <Typography
                          variant="strongSmallBody"
                          className={clsx(
                            "hidden max-w-15 sm:block",
                            styles.text.label
                          )}
                        >
                          {item.label}
                        </Typography>
                        <Typography
                          variant="smallBody"
                          className={clsx("px-1.75", styles.text.free)}
                        >
                          {item.free}
                        </Typography>
                        <Typography
                          variant={isSmDown ? "smallBody" : "strongSmallBody"}
                          className={clsx("px-1.75", styles.text.plus)}
                        >
                          {item.plus}
                        </Typography>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div
            // padding-top is to provide more faded area in footer
            className={clsx(
              "fixed bottom-0 z-10 flex w-full flex-col-reverse items-center justify-end gap-2 border-0 px-1.5 pb-2.5 pt-6 sm:flex-row sm:px-4 sm:pb-5",
              styles.footer
            )}
          >
            <a
              href={getSuraasaPlusLandingPageUrl()}
              target="_blank"
              rel="noreferrer"
              onClick={e => {
                if (!isNewUser) {
                  e.preventDefault()
                  handleClose()
                }
              }}
            >
              <Button
                variant="text"
                className={clsx(
                  "w-full shrink-0 p-1.5 sm:w-fit",
                  styles.buttons.text
                )}
              >
                {isNewUser ? "Learn More" : "Okay"}
              </Button>
            </a>
            <AsyncBuilder
              handler={async () => {
                if (!isNewUser) {
                  handleClose()
                  return
                }
                await onClick()
              }}
              render={props => (
                <Button
                  onClick={props.onClick}
                  loading={props.loading}
                  variant="filled"
                  className={clsx(
                    styles.buttons.primary,
                    "w-full shrink-0 rounded-[10px] px-2 py-1.5 font-semibold sm:w-fit"
                  )}
                >
                  {/* <Typography variant="strong">
                    {!isNewUser
                      ? "Discover What's Inside"
                      : isFreeTrialAvailable
                      ? "Start 7-day free trial"
                      : "Get Suraasa Plus"}
                  </Typography> */}

                  <Typography variant="strong">Get Suraasa Plus</Typography>
                </Button>
              )}
            />
          </div>
        </>
      }
      bodyClassName={clsx(styles.body, "p-0")}
      open={open}
      handleClose={handleClose}
      contentClassName="overflow-hidden rounded-xl max-w-2xl"
    />
  )
}

export default FeatureOverviewDialog
